import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";

import { IPartBin } from "../../models/PartBin";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import { naturalCompare } from "../../utils/naturalSort";
import printHtml from "../../utils/printHtml";

const colHelper = createColumnHelper<IPartBin>();

export default function PartBinsTable({ partBins, setPartBin }: { partBins: IPartBin[], setPartBin: (partBin: IPartBin) => void }) {
  const { t } = useTranslation("stock");
  // Default to name ascending, matching the order the API already returns them in.
  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);
  const { generatePartBinLabel } = useLabelGenerator();

  const cols = useMemo(() => [
    TableCheckboxCol(false),
    colHelper.display({
      id: "name",
      header: t("Name"),
      // Display column with no accessor, so "auto" has nothing to read. Compare the row explicitly,
      // naturally, so "B2" precedes "B10".
      sortingFn: (a, b) => naturalCompare(a.original.name, b.original.name),
      cell: (info) => (
        <div className="flex grow">
          <span className="ml-4 m-auto">{info.row.original.name}</span>
        </div>
      )
    }),
    colHelper.display({
      id: "partCount",
      header: t("Parts"),
      sortingFn: (a, b) => (a.original.partCount ?? 0) - (b.original.partCount ?? 0),
      cell: (info) => (
        <div className="flex grow">
          <span className="ml-4 m-auto">{info.row.original.partCount ?? 0}</span>
        </div>
      )
    })
  ], [t]);

  const table = useReactTable({
    data: partBins,
    columns: cols,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleMultiPrint = async () => {
    const selected = table.getSelectedRowModel().flatRows.map((row) => row.original);

    const htmlPages = await Promise.all(selected.map((partBin) => generatePartBinLabel(partBin)));

    // Stitch together with a page break inbetween each
    const allHtml = htmlPages.join("<div style='page-break-after: always;'></div>");

    printHtml(allHtml);

    table.resetRowSelection();
  };

  return (
    <>
      <div className="mt-4 bg-white overflow-auto space-y-4">
        <div>
          <ButtonInput
            label={t("Print Multiple")}
            isSubmit={false}
            onClick={handleMultiPrint}
            disabled={table.getSelectedRowModel().flatRows.length === 0}
            classes={undefined}
          />
        </div>
        <table className="min-w-full overflow-scroll divide-y border divide-gray-300">

          {/* Table headings - very similar to all the other tables */}
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="flex first:w-12 py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {/* Body */}
          <tbody>
            {table.getRowModel().rows.map((row, i) => (
              <Fragment key={i}>
                <tr
                  key={row.id}
                  onClick={() => setPartBin(row.original)}
                  className={"hover:bg-gray-100 hover:cursor-pointer"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-3 border text-sm">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              </Fragment>
            ))}
          </tbody>

        </table>
      </div>
    </>
  );
}
